import { Component, ErrorInfo, ReactNode } from "react";
import { Box, Button, Typography } from "@mui/material";
import { recordFreezeEvent } from "src/utils/freezeMonitor";

interface IWindowErrorBoundaryProps {
  children: ReactNode;
}

interface IWindowErrorBoundaryState {
  error: Error | null;
}

/** Contains a render error to its window, so the rest of the app keeps working. */
export class WindowErrorBoundary extends Component<IWindowErrorBoundaryProps, IWindowErrorBoundaryState> {
  state: IWindowErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): IWindowErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    const errorMessage: string = "A window failed to render";
    console.error(errorMessage, error, info.componentStack);
    recordFreezeEvent(errorMessage, { message: error.message, componentStack: info.componentStack });
  }

  render(): ReactNode {
    if (!this.state.error) {
      return this.props.children;
    }
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          This window ran into an error and any unsaved changes in it were lost. Reload it to start again, or close it.
        </Typography>
        <Button variant="outlined" size="small" onClick={() => this.setState({ error: null })}>
          Reload Window
        </Button>
      </Box>
    );
  }
}
